import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { BulkClaimLinkPreview, BulkClaimLinkResult, FirstSendScopeBody } from '../../api/types';
import { useV07Commands } from '../data/commands-context';
import {
  BULK_CLAIM_LINK_IMPACT_ROWS,
  bulkClaimLinkOutcomeText,
  bulkClaimLinkReasonText,
  bulkClaimLinkRevocationWarning,
} from '../lib/bulk-claim-link';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';

/**
 * CREATING ACCEPTANCE LINKS FOR THE SELECTED RECORDS — the impact first, then the approval.
 *
 * A record whose acceptance link was minted before the links were stored holds an address nobody
 * can read back: only its hash was kept, so the register's download prints an empty cell with a
 * reason for it. The only honest remedy is a NEW link, and that costs whoever may be holding the
 * old one — which is why this window exists at all. It is not a confirmation dialog with a count
 * in it; it is the place the cost is stated before anything happens.
 *
 * WHAT IT SHOWS, IN THIS ORDER
 * ----------------------------
 * The seven numbers, from the SERVER. A browser counting the rows it happens to be holding is how
 * a total nobody can reconcile gets onto a screen, and these numbers are an approval rather than an
 * illustration. Then, separately and last, the one number that asks for a decision: how many
 * records will lose a link that works today.
 *
 * THE SEPARATE APPROVAL
 * ---------------------
 * While that number is above zero the act cannot be started at all: the box has to be ticked, and
 * the sentence beside it says what cannot be known — whether anybody is holding one of those
 * links. The raw value was never stored, so the product cannot say, and the honest sentence is the
 * one that admits it. Ticking is also sent to the server as its own field, which refuses those
 * records one by one without it; this window is the first of two gates, not the only one.
 *
 * THE APPROVAL IS FOR ONE WORLD
 * -----------------------------
 * The preview's `digest` goes back with the act. If anything about those records moved in between —
 * a token rotated, a link lapsed, somebody accepted — the server refuses rather than acting on a
 * stale picture, and this window says so and offers to read the numbers again. That refusal is not
 * an error to be worked around: it is the reason the numbers can be trusted.
 *
 * WHAT IT NEVER SHOWS: a link. Not even for the records it just created one for. The act's answer
 * carries none, because the links leave the building through one door only — the authorized CSV
 * download — and the result says exactly that.
 */
export function BulkClaimLinkPanel({
  scope,
  scopeLabel,
  onClose,
  onLinksCreated,
}: {
  scope: FirstSendScopeBody;
  /** «Seçili 109 kayıt» or «Filtreye uyan 109 kayıt» — named by the bar that opened this. */
  scopeLabel: string;
  onClose: () => void;
  /** Told once, after a run that wrote something, so the register behind re-reads. */
  onLinksCreated?: (() => void) | undefined;
}): ReactElement {
  const commands = useV07Commands();
  const toast = useToast();
  const [preview, setPreview] = useState<BulkClaimLinkPreview | null>(null);
  const [result, setResult] = useState<BulkClaimLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  /*
   * `alive` is written TRUE on mount and not only cleared on unmount: React's StrictMode runs a
   * rehearsal mount and unmount before the real one, and a flag that is only ever set to false
   * there would drop every answer for the whole life of the real panel. That defect was measured
   * once in `FirstSendPanel` and is not repeated here.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await commands.previewBulkClaimLinks({ scope });
    if (!alive.current) return;
    setLoading(false);
    if (r.ok && r.preview) {
      setPreview(r.preview);
      /* A fresh picture is a fresh decision: an approval ticked for the old one does not carry. */
      setConfirmed(false);
    } else setError(r.message ?? 'Etki analizi okunamadı.');
  }, [commands, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async () => {
    if (!preview) return;
    setRunning(true);
    setError(null);
    const r = await commands.createBulkClaimLinks({
      scope,
      expectDigest: preview.digest,
      confirmRevocations: confirmed,
    });
    if (!alive.current) return;
    setRunning(false);
    if (r.ok && r.result) {
      setResult(r.result);
      toast(`${r.result.created} kabul bağlantısı oluşturuldu`, 'ok');
      /*
       * Only after a run that actually wrote something, and only through the owner screen's own
       * read: a record whose token rotated is a record whose claim state on screen is stale.
       */
      if (r.result.created > 0) onLinksCreated?.();
    } else {
      /* The server's own sentence, including the one that says the approval is out of date. */
      setError(r.message ?? 'Kabul bağlantıları oluşturulamadı.');
    }
  };

  const impact = result?.impact ?? preview?.impact ?? null;
  const revoking = preview?.impact.revoking ?? 0;
  const needsApproval = revoking > 0 && !confirmed;
  /*
   * NOTHING FOR THIS ACT TO DO, which is not the same as «nothing is wrong».
   *
   * Reuse is a real outcome — it is how a second run keeps the links it already produced — but it
   * writes nothing, so a run consisting only of reuses is a request that changes no record. The
   * operator's next step then is the download, and the window says so instead of offering a button
   * whose press would be a no-op.
   */
  const nothingToDo = preview !== null && preview.impact.needsNewLink === 0;
  const title = result ? 'Kabul bağlantısı sonucu' : 'Kabul bağlantısı oluştur';

  return (
    <Modal label={title} onClose={onClose} wide>
      <ModalHead title={title} onClose={onClose} />
      <div className="card-pad" id="clPanel">
        <p className="muted" id="clScope">
          {scopeLabel}
        </p>
        {/*
         * THE ONE SENTENCE ABOUT E-MAIL, said before anything else is read: this act does not send.
         * An operator who has just used the send bar two rows above needs to know which of the two
         * they are in, and «no e-mail» is the difference that matters most.
         */}
        <p className="muted small" id="clRule">
          Bu işlem yalnız kabul bağlantısı üretir ve şifreli saklar. Hiçbir e-posta gönderilmez; bağlantılar
          kayıt listesinin «Dışa aktar» dosyasından indirilir.
        </p>
        {error === null ? null : (
          <p className="danger" id="clError" role="status">
            {error}
          </p>
        )}
        {loading ? (
          <p className="muted" id="clLoading">
            Etki analizi okunuyor…
          </p>
        ) : null}
        {/* A plain paragraph, because `row gap` is nothing outside the contexts the reference
            scopes it to — measured: it left the button exactly where a block paragraph puts it. */}
        {impact === null || result !== null ? null : (
          <p>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="clReload"
              disabled={loading || running}
              onClick={() => void load()}
            >
              Etki analizini yeniden oku
            </button>
          </p>
        )}
        {impact === null ? null : (
          <table className="table" id="clCounts">
            <tbody>
              {BULK_CLAIM_LINK_IMPACT_ROWS.map((row) => (
                <tr key={row.key}>
                  <th scope="row">{row.label}</th>
                  <td id={`clImpact_${row.key}`}>{impact[row.key]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {preview !== null && preview.skipped.length > 0 ? (
          <>
            <h3>Bağlantı üretilmeyecek kayıtlar</h3>
            <ul id="clSkipped">
              {preview.skipped.map((s) => (
                <li key={s.reason} data-reason={s.reason}>
                  {`${s.count} kayıt — ${bulkClaimLinkReasonText(s.reason)}`}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {preview?.truncated ? (
          <p className="danger" id="clTruncated">
            {`Kapsam ${preview.limit} kayıtla sınırlandı; filtreyi daraltın.`}
          </p>
        ) : null}
        {/*
         * THE SEPARATE APPROVAL. Drawn only while there is something to approve, and the act's
         * button stays disabled until it is ticked — so the destructive half of this act cannot be
         * reached by pressing the same button twice.
         */}
        {result === null && revoking > 0 ? (
          <>
            <p className="warn" id="clRevokeWarn" role="status">
              {bulkClaimLinkRevocationWarning(revoking)}
            </p>
            <label className="field row gap" htmlFor="clConfirmRevoke">
              <input
                id="clConfirmRevoke"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>{`${revoking} kaydın mevcut kabul bağlantısının iptal edilmesini onaylıyorum`}</span>
            </label>
          </>
        ) : null}
        {result === null && nothingToDo ? (
          <p className="muted" id="clNone">
            {(preview?.impact.reusable ?? 0) > 0
              ? `Seçilen kayıtların ${preview?.impact.reusable} tanesinin kabul bağlantısı zaten saklı; yeni bağlantı üretilmeyecek. Bağlantıları «Dışa aktar» dosyasından indirin.`
              : 'Seçilen kayıtların hiçbiri için bağlantı üretilmeyecek.'}
          </p>
        ) : null}
        {result === null ? null : (
          <>
            <h3>İşlem sonucu</h3>
            <p id="clOutcome">
              {`${result.created} yeni bağlantı üretildi · ${result.reused} kaydın bağlantısı korundu` +
                (result.failed > 0 ? ` · ${result.failed} kayıtta işlem tamamlanamadı` : '')}
            </p>
            {/*
             * WHICH RECORDS COMPLETED, one line each and by public code: a run that did eighty of a
             * hundred and nine has to be readable as exactly that, and «hangi kayıtlar tamamlandı»
             * is a question about records rather than about a number.
             */}
            <ul id="clRecords">
              {result.records.map((r) => (
                <li key={r.credentialId} data-outcome={r.outcome}>
                  {`${r.publicCode} — ${bulkClaimLinkOutcomeText(r.outcome)}`}
                </li>
              ))}
            </ul>
            <p className="muted small" id="clNextStep">
              Bağlantıları indirmek için kayıt listesindeki «Dışa aktar» düğmesini kullanın.
            </p>
          </>
        )}
      </div>
      {/*
       * TWO BUTTONS, which is what every window in this design has.
       *
       * The re-read control was here as a third, and at 390px it did not fit: measured, the three
       * labels need 489px of the 310px a 350px window leaves, so `.card-foot`'s own `flex-wrap`
       * put them on two and three lines with a 215px button alone on the last one — where the
       * design's own «Tek credential oluştur» window is one 65px row of two 36px buttons. So the
       * re-read moved to the numbers it re-reads, in the body, and the footer is the design's
       * shape again: a ghost that closes and a primary that acts.
       */}
      <div className="card-foot row gap">
        <button className="btn btn-ghost" type="button" onClick={onClose} id="clClose">
          Kapat
        </button>
        {result === null ? (
          <button
            className="btn btn-primary"
            type="button"
            id="clRun"
            disabled={loading || running || preview === null || needsApproval || nothingToDo}
            onClick={() => void run()}
          >
            {running ? 'Oluşturuluyor…' : 'Kabul bağlantılarını oluştur'}
          </button>
        ) : null}
      </div>
    </Modal>
  );
}
